import React from "react";
import "@/App.css";
import {
    BrowserRouter,
    Routes,
    Route,
    Navigate,
} from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import Home from "@/pages/Home";
import ChangePassword from "@/pages/ChangePassword";
import AcceptInvite from "@/pages/AcceptInvite";
import AdminUsers from "@/pages/admin/Users";
import AdminProjects from "@/pages/admin/Projects";
import AdminCustomers from "@/pages/admin/Customers";
import AdminShifts from "@/pages/admin/Shifts";
import Roster from "@/pages/Roster";
import Leaves from "@/pages/Leaves";
import Handovers from "@/pages/Handovers";
import HandoverDetail from "@/pages/HandoverDetail";
import Reports from "@/pages/Reports";
import ProjectTasks from "@/pages/ProjectTasks";

const ADMIN_ROLES = new Set(["Super Admin", "Admin"]);
const LEAD_PLUS_ROLES = new Set(["Super Admin", "Admin", "Project Manager", "Team Lead"]);

function LoadingScreen() {
    return (
        <div
            className="min-h-screen flex items-center justify-center bg-ntt-surface"
            data-testid="loading-screen"
        >
            <div className="flex flex-col items-center gap-3">
                <div className="h-9 w-9 rounded-full border-2 border-[#0072BC] border-t-transparent animate-spin" />
                <p className="text-[13px] text-ntt-muted">Loading…</p>
            </div>
        </div>
    );
}

function ProtectedRoute({ children, allowForcePasswordChange = false }) {
    const { user, loading } = useAuth();
    if (loading) return <LoadingScreen />;
    if (!user) return <Navigate to="/login" replace />;
    if (user.force_password_change && !allowForcePasswordChange) {
        return <Navigate to="/change-password" replace />;
    }
    return children;
}

function AdminRoute({ children }) {
    const { user, loading } = useAuth();
    if (loading) return <LoadingScreen />;
    if (!user) return <Navigate to="/login" replace />;
    if (user.force_password_change) return <Navigate to="/change-password" replace />;
    if (!ADMIN_ROLES.has(user.role)) return <Navigate to="/" replace />;
    return children;
}

function LeadRoute({ children }) {
    const { user, loading } = useAuth();
    if (loading) return <LoadingScreen />;
    if (!user) return <Navigate to="/login" replace />;
    if (user.force_password_change) return <Navigate to="/change-password" replace />;
    if (!LEAD_PLUS_ROLES.has(user.role)) return <Navigate to="/" replace />;
    return children;
}

function PublicOnly({ children }) {
    const { user, loading } = useAuth();
    if (loading) return <LoadingScreen />;
    if (user) {
        if (user.force_password_change) {
            return <Navigate to="/change-password" replace />;
        }
        return <Navigate to="/" replace />;
    }
    return children;
}

export default function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <Routes>
                        <Route
                            path="/login"
                            element={
                                <PublicOnly>
                                    <Login />
                                </PublicOnly>
                            }
                        />
                        <Route
                            path="/accept-invite/:token"
                            element={<AcceptInvite />}
                        />
                        <Route
                            path="/change-password"
                            element={
                                <ProtectedRoute allowForcePasswordChange>
                                    <ChangePassword />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/users"
                            element={
                                <AdminRoute>
                                    <AdminUsers />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/projects"
                            element={
                                <AdminRoute>
                                    <AdminProjects />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/customers"
                            element={
                                <AdminRoute>
                                    <AdminCustomers />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/shifts"
                            element={
                                <AdminRoute>
                                    <AdminShifts />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/tasks"
                            element={
                                <ProtectedRoute>
                                    <ProjectTasks />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/tasks/:project_id"
                            element={
                                <ProtectedRoute>
                                    <ProjectTasks />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/roster"
                            element={
                                <ProtectedRoute>
                                    <Roster />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/leaves"
                            element={
                                <ProtectedRoute>
                                    <Leaves />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/handovers"
                            element={
                                <ProtectedRoute>
                                    <Handovers />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/handovers/:id"
                            element={
                                <ProtectedRoute>
                                    <HandoverDetail />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/reports"
                            element={
                                <LeadRoute>
                                    <Reports />
                                </LeadRoute>
                            }
                        />
                        <Route
                            path="/"
                            element={
                                <ProtectedRoute>
                                    <Home />
                                </ProtectedRoute>
                            }
                        />
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                    <Toaster
                        position="top-right"
                        richColors
                        closeButton
                        toastOptions={{
                            style: { fontFamily: "Inter, Arial, sans-serif" },
                        }}
                    />
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}
